// videoCompression.ts - Utility for compressing screen recordings

export interface VideoCompressionOptions {
  targetSizePercent?: number; // Target size as percentage (0.3 = 30%)
  maxWidth?: number;
  maxHeight?: number;
  videoBitrate?: number; // in bps
  audioBitrate?: number; // in bps
  frameRate?: number;
}

export interface CompressionResult {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  duration: number;
}

const DEFAULT_OPTIONS: VideoCompressionOptions = {
  targetSizePercent: 0.35, // Target 35% of original (30-40% range)
  maxWidth: 1280,
  maxHeight: 720,
  videoBitrate: 1000000, // 1 Mbps
  audioBitrate: 64000, // 64 kbps
  frameRate: 24,
};

/**
 * Calculate optimal bitrate based on target file size
 */
function calculateOptimalBitrate(
  originalSize: number,
  duration: number,
  targetPercent: number
): number {
  const targetSize = originalSize * targetPercent;
  // Convert to bits and divide by duration
  const targetBitrate = (targetSize * 8) / duration;
  // Ensure minimum quality (500 kbps) and max (2 Mbps)
  return Math.max(500000, Math.min(targetBitrate, 2000000));
}

/**
 * Check if compression is needed based on file size
 */
export function needsCompression(blob: Blob, thresholdMB: number = 5): boolean {
  const sizeInMB = blob.size / (1024 * 1024);
  return sizeInMB > thresholdMB;
}

/**
 * Get video dimensions from blob
 */
async function getVideoDimensions(
  videoUrl: string
): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
      URL.revokeObjectURL(video.src);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };
    video.src = videoUrl;
  });
}

/**
 * Calculate scaled dimensions maintaining aspect ratio
 */
function calculateScaledDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  const aspectRatio = width / height;
  let newWidth = width;
  let newHeight = height;

  if (width > maxWidth) {
    newWidth = maxWidth;
    newHeight = Math.round(maxWidth / aspectRatio);
  }
  if (newHeight > maxHeight) {
    newHeight = maxHeight;
    newWidth = Math.round(maxHeight * aspectRatio);
  }
  // Ensure dimensions are even (required for many codecs)
  newWidth = Math.floor(newWidth / 2) * 2;
  newHeight = Math.floor(newHeight / 2) * 2;

  return { width: newWidth, height: newHeight };
}

/**
 * Compress video using Canvas and MediaRecorder
 * This method re-encodes the video at lower quality
 */
export async function compressVideo(
  inputBlob: Blob,
  options: VideoCompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const originalSize = inputBlob.size;

  console.log("🗜️ Starting video compression:", {
    originalSize: (originalSize / (1024 * 1024)).toFixed(2) + " MB",
    targetPercent: (opts.targetSizePercent! * 100).toFixed(0) + "%",
  });

  const inputUrl = URL.createObjectURL(inputBlob);

  try {
    // Get video metadata
    const { width, height, duration } = await getVideoDimensions(inputUrl);
    console.log("📐 Original dimensions:", { width, height, duration });

    // Calculate scaled dimensions
    const scaled = calculateScaledDimensions(
      width,
      height,
      opts.maxWidth!,
      opts.maxHeight!
    );
    console.log("📐 Scaled dimensions:", scaled);

    // Calculate optimal bitrate for target size
    const optimalBitrate = calculateOptimalBitrate(
      originalSize,
      duration,
      opts.targetSizePercent!
    );
    console.log("📊 Optimal bitrate:", (optimalBitrate / 1000000).toFixed(2) + " Mbps");

    // Create video element for playback
    const video = document.createElement("video");
    video.src = inputUrl;
    video.muted = true;
    video.playsInline = true;

    // Create canvas for rendering
    const canvas = document.createElement("canvas");
    canvas.width = scaled.width;
    canvas.height = scaled.height;
    const ctx = canvas.getContext("2d")!;

    // Wait for video to be ready
    await new Promise<void>((resolve, reject) => {
      video.oncanplaythrough = () => resolve();
      video.onerror = () => reject(new Error("Video load failed"));
      video.load();
    });

    // Create MediaStream from canvas
    const canvasStream = canvas.captureStream(opts.frameRate!);

    // Try to get audio from original video
    let combinedStream: MediaStream;
    try {
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaElementSource(video);
      const dest = audioCtx.createMediaStreamDestination();
      source.connect(dest);
      source.connect(audioCtx.destination);

      combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);
    } catch (e) {
      console.warn("⚠️ Could not extract audio, video-only compression");
      combinedStream = canvasStream;
    }

    // Determine best codec
    let mimeType = "video/webm;codecs=vp8";
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
      mimeType = "video/webm;codecs=vp9";
    }
    if (MediaRecorder.isTypeSupported("video/mp4;codecs=h264")) {
      mimeType = "video/mp4;codecs=h264";
    }

    // Create MediaRecorder with compressed settings
    const mediaRecorder = new MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: optimalBitrate,
      audioBitsPerSecond: opts.audioBitrate,
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // Start recording and playback
    const compressionPromise = new Promise<Blob>((resolve, reject) => {
      mediaRecorder.onstop = () => {
        const compressedBlob = new Blob(chunks, { type: mimeType.split(";")[0] });
        resolve(compressedBlob);
      };
      mediaRecorder.onerror = (e) => reject(e);
    });

    mediaRecorder.start(100);
    video.currentTime = 0;
    await video.play();

    // Render frames to canvas
    const renderFrame = () => {
      if (video.ended || video.paused) {
        mediaRecorder.stop();
        return;
      }
      ctx.drawImage(video, 0, 0, scaled.width, scaled.height);
      requestAnimationFrame(renderFrame);
    };
    renderFrame();

    // Wait for video to end
    await new Promise<void>((resolve) => {
      video.onended = () => resolve();
    });

    // Get compressed blob
    const compressedBlob = await compressionPromise;
    const compressedSize = compressedBlob.size;
    const compressionRatio = compressedSize / originalSize;

    console.log("✅ Compression complete:", {
      originalSize: (originalSize / (1024 * 1024)).toFixed(2) + " MB",
      compressedSize: (compressedSize / (1024 * 1024)).toFixed(2) + " MB",
      compressionRatio: (compressionRatio * 100).toFixed(1) + "%",
      savedSpace: ((1 - compressionRatio) * 100).toFixed(1) + "%",
    });

    return {
      blob: compressedBlob,
      originalSize,
      compressedSize,
      compressionRatio,
      duration,
    };
  } finally {
    URL.revokeObjectURL(inputUrl);
  }
}

/**
 * Quick compression using reduced quality re-encoding
 * Faster but less control over final size
 */
export async function quickCompressVideo(
  inputBlob: Blob,
  qualityFactor: number = 0.35 // 0.3-0.4 for 30-40% of original
): Promise<CompressionResult> {
  const originalSize = inputBlob.size;
  const inputUrl = URL.createObjectURL(inputBlob);

  try {
    const { duration } = await getVideoDimensions(inputUrl);
    
    // Calculate target bitrate based on original size and quality factor
    const originalBitrate = (originalSize * 8) / duration;
    const targetBitrate = Math.max(500000, originalBitrate * qualityFactor);

    console.log("🗜️ Quick compression:", {
      originalBitrate: (originalBitrate / 1000000).toFixed(2) + " Mbps",
      targetBitrate: (targetBitrate / 1000000).toFixed(2) + " Mbps",
    });

    return await compressVideo(inputBlob, {
      targetSizePercent: qualityFactor,
      videoBitrate: targetBitrate,
    });
  } finally {
    URL.revokeObjectURL(inputUrl);
  }
}

/**
 * Estimate compressed size without actually compressing
 */
export function estimateCompressedSize(
  originalSize: number,
  targetPercent: number = 0.35
): number {
  return Math.round(originalSize * targetPercent);
}

/**
 * Get compression statistics
 */
export function getCompressionStats(result: CompressionResult): string {
  const savedMB = (result.originalSize - result.compressedSize) / (1024 * 1024);
  const savedPercent = ((1 - result.compressionRatio) * 100).toFixed(1);
  return `Compressed from ${(result.originalSize / (1024 * 1024)).toFixed(2)}MB to ${(result.compressedSize / (1024 * 1024)).toFixed(2)}MB (saved ${savedMB.toFixed(2)}MB / ${savedPercent}%)`;
}